// File: src/components/admin/AdminLayout.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Main layout component for the admin portal with sidebar navigation

import React from 'react';
import { Outlet, useLocation, NavLink } from 'react-router-dom';
import {
    Box, Drawer, AppBar, Toolbar, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Typography, CssBaseline, Button, Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { People as PeopleIcon, ExitToApp as ExitToAppIcon, Settings as SettingsIcon, Extension as ExtensionIcon, CardMembership as CardMembershipIcon, Email as EmailIcon, Description as DescriptionIcon, FactCheck as AuditIcon, Schedule as ScheduleIcon, SmartToy as SmartToyIcon, AutoFixHigh as AutoFixHighIcon, Phone as PhoneIcon, Sync as SyncIcon, Dashboard as DashboardIcon, MonetizationOn as MoneyIcon, TrendingDown as ChurnIcon, Public as PublicIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import { useAuth } from '../../app/AuthContext';

const drawerWidth = 240;

// Shared active-link styling for sidebar nav items.
const activeLinkSx = {
    '&.active': {
        backgroundColor: 'action.selected',
        color: 'primary.main',
        '& .MuiListItemIcon-root': { color: 'primary.main' },
    },
};

/** Renders a single sidebar nav link. */
const NavItem = ({ to, icon, label }) => (
    <ListItem disablePadding>
        <ListItemButton component={NavLink} to={to} sx={activeLinkSx}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
        </ListItemButton>
    </ListItem>
);

/** Renders a section header label in the sidebar. */
const SectionLabel = ({ children }) => (
    <Typography
        variant="overline"
        sx={{ px: 2, pt: 2, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.68rem', letterSpacing: '0.08em' }}
    >
        {children}
    </Typography>
);

const AdminLayout = () => {
    const { user, signOut } = useAuth();
    const location = useLocation();

    const getPageTitle = () => {
        if (location.pathname.startsWith('/users/')) {
            return 'User Details';
        }
        if (location.pathname.startsWith('/users')) {
            return 'User Management';
        }
        if (location.pathname.startsWith('/edenai-config')) {
            return 'EdenAI Configuration';
        }
        if (location.pathname.startsWith('/integrations')) {
            return 'Integrations';
        }
        if (location.pathname.startsWith('/geo')) {
            return 'Geo Target Registry';
        }
        if (location.pathname.startsWith('/subscription-options')) {
            return 'Subscription Options';
        }
        if (location.pathname.startsWith('/subscription/credits')) {
            return 'Credit Grants';
        }
        if (location.pathname.startsWith('/subscription/users')) {
            return 'Subscription Users';
        }
        if (location.pathname.startsWith('/subscription')) {
            return 'Subscription Overview';
        }
        if (location.pathname === '/plans') {
            return 'Plans';
        }
        if (location.pathname.startsWith('/promos')) {
            return 'Promo Codes';
        }
        if (location.pathname.startsWith('/email-config')) {
            return 'Email Configuration';
        }
        if (location.pathname.startsWith('/documentation')) {
            return 'Documentation';
        }
        if (location.pathname.startsWith('/audit')) {
            return 'Audit Logs';
        }
        if (location.pathname.startsWith('/scheduler')) {
            return 'Scheduler Management';
        }
        if (location.pathname.startsWith('/syncs')) {
            return 'Sync Observability';
        }
        if (location.pathname.startsWith('/analysis-tasks')) {
            return 'AI Analysis Tasks';
        }
        if (location.pathname.startsWith('/otp-phones')) {
            return 'OTP Phone Management';
        }
        if (location.pathname.startsWith('/autopilot')) {
            return 'Autopilot Management';
        }
        return 'Admin Portal';
    };

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div">
                    Defo Ads Admin
                </Typography>
            </Toolbar>
            <Divider />

            {/* ─── Operations ─── */}
            <SectionLabel>Operations</SectionLabel>
            <List dense disablePadding>
                <NavItem to="/users" icon={<PeopleIcon />} label="Users" />
                <NavItem to="/otp-phones" icon={<PhoneIcon />} label="OTP Phones" />
                <NavItem to="/audit" icon={<AuditIcon />} label="Audit Logs" />
            </List>

            <Divider sx={{ my: 1 }} />

            {/* ─── Automation ─── */}
            <SectionLabel>Automation</SectionLabel>
            <List dense disablePadding>
                <NavItem to="/scheduler" icon={<ScheduleIcon />} label="Scheduler" />
                <NavItem to="/syncs" icon={<SyncIcon />} label="Syncs" />
                <NavItem to="/analysis-tasks" icon={<SmartToyIcon />} label="AI Tasks" />
                <NavItem to="/autopilot" icon={<AutoFixHighIcon />} label="Autopilot" />
            </List>

            <Divider sx={{ my: 1 }} />

            {/* ─── Subscription ─── */}
            <SectionLabel>Subscription</SectionLabel>
            <List dense disablePadding>
                { /* overview dashboard is the default Subscription destination. */}
                <NavItem to="/subscription" icon={<DashboardIcon />} label="Overview" />
                { /* canonical 5-field plans editor. */}
                <NavItem to="/plans" icon={<CardMembershipIcon />} label="Plans" />
                <NavItem to="/promos" icon={<LocalOfferIcon />} label="Promo Codes" />
                <NavItem to="/subscription/credits" icon={<MoneyIcon />} label="Credit grants" />
                <NavItem to="/subscription/users?state=PAID_CANCELLED" icon={<ChurnIcon />} label="Churn risk" />
            </List>

            <Divider sx={{ my: 1 }} />

            {/* ─── Configuration ─── */}
            <SectionLabel>Configuration</SectionLabel>
            <List dense disablePadding>
                <NavItem to="/edenai-config" icon={<SettingsIcon />} label="EdenAI Config" />
                <NavItem to="/email-config" icon={<EmailIcon />} label="Email Config" />
                <NavItem to="/integrations" icon={<ExtensionIcon />} label="Integrations" />
                <NavItem to="/geo" icon={<PublicIcon />} label="Geo Targets" />
                <NavItem to="/documentation" icon={<DescriptionIcon />} label="Documentation" />
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: `calc(100% - ${drawerWidth}px)`,
                    ml: `${drawerWidth}px`,
                    backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(12px)',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    paddingTop: 'env(safe-area-inset-top)',
                }}
            >
                <Toolbar>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {getPageTitle()}
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                        {user?.email || 'Admin'}
                    </Typography>
                    <Button
                        color="inherit"
                        startIcon={<ExitToAppIcon />}
                        onClick={signOut}
                        variant="outlined"
                        size="small"
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                anchor="left"
            >
                {drawer}
            </Drawer>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: 'grey.50',
                    p: 3,
                    minHeight: '100vh',
                }}
            >
                <Toolbar />
                <Outlet />
            </Box>
        </Box>
    );
};

export default AdminLayout;
